<template>
    <div class="eventwise-inline-edit">
        <FormField v-if="editable" :label="label">
            <Button v-if="!isEditing" type="is-link" class="tw-w-full !tw-text-left !tw-p-0" @click="toggleEdit()">
                <span class="tw-mr-1">{{ model ? displayValue : 'Add' }}</span>
                <i :class="model ? 'mdi mdi-pencil' : 'mdi mdi-plus'"></i>
            </Button>
            <Tooltip v-else-if="currency" :class="containerClasses" :triggers="['hover', 'focus']">
                <template #content>
                    <span class="key-prompt">
                        <i class="mdi mdi-keyboard-return"></i>
                        Enter
                    </span>
                    Save
                    <span class="key-prompt">
                        <i class="mdi mdi-keyboard-esc"></i>
                        Esc
                    </span>
                    Cancel
                </template>
                <Currency
                    v-model="model"
                    :currency-code="currencyCode"
                    @keyup.native.enter.stop="onEnter()"
                    @keyup.native.esc.prevent="toggleEdit()"
                />
            </Tooltip>
            <Tooltip v-else :class="containerClasses" :triggers="['hover', 'focus']">
                <template #content>
                    Press
                    <span class="key-prompt">
                        <i class="mdi mdi-keyboard-return"></i>
                        Enter
                    </span>
                    to save or
                    <span class="key-prompt">
                        <i class="mdi mdi-keyboard-esc"></i>
                        Esc
                    </span>
                    to cancel
                </template>
                <Textbox
                    v-model="model"
                    :size="size"
                    type="text"
                    @keyup.native.enter.exact="onEnter()"
                    @keyup.native.esc.prevent="toggleEdit()"
                />
            </Tooltip>
        </FormField>
        <FormField v-else :label="label">
            {{ displayValue }}
        </FormField>
    </div>
</template>
<script setup lang="ts" generic="TModel extends string | number | null = string | number | null">
import Button from '@/js/components/controls/Button.vue';
import Tooltip from '@/js/components/Tooltip.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import Currency from '@/js/components/controls/Currency.vue';
import { localise } from '@/js/utils.js';
import { computed, ref } from 'vue';

const props = defineProps({
    editable: {
        type: Boolean,
        default: true,
    },
    size: {
        type: String,
        required: false,
    },
    label: {
        type: String,
        default: '',
    },
    currency: {
        type: Boolean,
        default: false,
    },
    currencyCode: {
        type: String,
        default: null,
    },
    minValue: {
        type: Number,
        required: false,
    },
});
const model = defineModel<TModel>();
const containerClasses = 'w-100';
const emit = defineEmits<{
    updated: [TModel | undefined];
}>();
const isEditing = ref(false);
const displayValue = computed(() => {
    if (!model.value) {
        return '';
    }
    return props.currency ? localise(model.value, 2, props.currencyCode) : model.value;
});

const toggleEdit = () => {
    isEditing.value = !isEditing.value;
};
const onEnter = () => {
    isEditing.value = false;
    emit('updated', model.value);
};
</script>
<style scoped>
.eventwise-inline-edit {
    > .field {
        margin-bottom: 0 !important;

        > .label {
            margin-bottom: 1px !important;
        }

        > button {
            margin: 0 !important;
            padding: 0 !important;
            justify-content: flex-start !important;
            font-size: 100%;
            white-space: normal !important;
            font-family: 'Work Sans' !important;
        }
    }
}
</style>
